/**
 * Public quote acceptance page — /quote/:token
 * Client reviews the quote and signs using the same DocuSign-style block
 * as the intake wizard (typed name → cursive Dancing Script preview →
 * cryptographically salted document hash on submit).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { PawPrint, Loader2, CheckCircle2, X } from 'lucide-react';
import { QuotePricingTable } from '../components/QuotePricingTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteAcceptance() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typed, setTyped] = useState('');
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(null);

  useEffect(() => {
    axios.get(`${API}/quote/${token}`)
      .then(({ data }) => { setQuote(data); setLoading(false); })
      .catch((e) => { setError(e?.response?.data?.detail || 'Quote not available.'); setLoading(false); });
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/quote/${token}/accept`, { typed_name: typed, accept_terms: agree });
      setSigned(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not accept the quote.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Center><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></Center>;
  if (error && !quote) return <Center><div className="bg-white p-8 rounded-2xl shadow max-w-md text-center"><X className="w-12 h-12 mx-auto text-red-500 mb-3" /><h1 className="text-xl font-semibold mb-2">Quote unavailable</h1><p className="text-gray-600">{error}</p></div></Center>;

  const alreadyAccepted = quote.status === 'accepted' || quote.status === 'paid' || !!quote.accepted_at;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 pb-24">
      <header className="bg-white border-b border-emerald-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <PawPrint className="w-7 h-7 text-emerald-600" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-gray-900 truncate">Pawsome Ventures — Quote {quote.quote_number || ''}</div>
            <div className="text-xs text-gray-500 truncate">{alreadyAccepted ? 'Signed — thank you!' : 'Review & sign to accept'}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="quote-acceptance">
        {/* Summary */}
        <section className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-5 sm:p-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Prepared for" value={quote.client_name} />
            <Info label="Pets" value={(quote.pet_names || []).join(', ')} />
            <Info label="Origin" value={quote.origin} />
            <Info label="Destination" value={quote.destination} />
            <Info label="Travel Date" value={quote.travel_date || 'TBD'} />
            <Info label="Valid Until" value={(quote.valid_until || '').slice(0, 10)} />
          </div>
        </section>

        {/* Line items */}
        <QuotePricingTable quote={quote} />

        {quote.terms && (
          <section className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-5 sm:p-6">
            <h3 className="text-sm font-semibold text-emerald-700 mb-2">Terms &amp; Conditions</h3>
            <div className="text-sm text-gray-700 whitespace-pre-wrap">{quote.terms}</div>
          </section>
        )}

        {/* Signature */}
        {alreadyAccepted || signed ? (
          <section className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600 mb-3" />
            <h2 className="text-lg font-bold text-gray-900 mb-1">Quote accepted</h2>
            <p className="text-sm text-gray-600">Signed by <b>{quote.accepted_by || signed?.typed_name || typed}</b></p>
            {(signed?.signed_at || quote.accepted_at) && (
              <p className="text-xs text-gray-500 mt-1">on {(signed?.signed_at || quote.accepted_at).slice(0, 19).replace('T', ' ')} UTC</p>
            )}
            {signed?.document_id && (
              <p className="text-xs text-gray-500 mt-1">Document ID: {signed.document_id}</p>
            )}
          </section>
        ) : (
          <section className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-5 sm:p-6">
            <h3 className="text-sm font-semibold text-emerald-700 mb-3">Accept &amp; Sign</h3>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-800 mb-4">
              <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-testid="quote-agree" />
              <span>I have read and agree to the terms &amp; conditions of this quote.</span>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-gray-600 mb-1">Type your full legal name to sign *</span>
              <input value={typed} onChange={e => setTyped(e.target.value)} placeholder="Full name" className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none" data-testid="quote-sign-input" />
            </label>
            {typed.trim().length >= 3 && (
              <div className="mt-4 border border-emerald-100 rounded-xl p-4 bg-emerald-50/50">
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-1">Preview — DocuSign-style signature</div>
                <div style={{ fontFamily: '"Dancing Script", cursive', fontSize: '32px', color: '#1f2937' }}>{typed.split(',')[0].trim()}</div>
                <div className="border-t border-gray-300 my-2" />
                <div className="text-sm font-semibold text-gray-800">{typed}</div>
                <div className="text-xs text-gray-500 italic mt-1">Will be timestamped in EST · document hash generated on submit</div>
              </div>
            )}
            {error && <div className="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">{error}</div>}
            <button
              onClick={submit}
              disabled={submitting || !agree || typed.trim().length < 3}
              className="mt-5 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="quote-submit-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing…</> : <>Accept &amp; sign quote</>}
            </button>
          </section>
        )}
      </main>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center bg-emerald-50 p-6">{children}</div>;
const Info = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-gray-900 font-medium">{value || '—'}</div>
  </div>
);
